import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CallLog } from "@/services/call.service";
import { useCallDetails } from "@/services/call.query";
import { fixTranscript, getToolExecutions } from "@/services/call.service";
import {
  Wrench, CheckCircle2, Hash, Type, ToggleLeft, List,
  FileText, Calendar, Phone, Clock, User, MessageSquare,
  Send, Reply, ChevronDown, Video, Volume2, Copy, Check, RefreshCw, Loader2,
  AlertTriangle
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDisconnectionReason, getDisconnectionReasonInfo } from "@/utils/disconnection-reasons";
import { isSearchWithoutSources, NO_SOURCES_TEXT } from "@/utils/google-search-card";
import { TwilioRecording } from "./TwilioRecording";
import { WebhookEventsTab } from "./WebhookEventsTab";

interface CallDetailsDialogProps {
  call: CallLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefresh?: () => void;
}

interface ToolExecution {
  id: string;
  tool_name: string;
  tool_type: string;
  description: string | null;
  args: any;
  result: any;
  error: string | null;
  executed_at: string;
  status_code: number | null;
  status: string | null;
}

interface TranscriptItem {
  id: string;
  role: 'User' | 'Agent' | 'Tool';
  text: string;
  timestamp?: string;
  toolName?: string;
  toolArgs?: any;
  toolResult?: any;
  toolError?: string;
  isToolExecution?: boolean;
  toolStatusCode?: number | null;
  documents?: Array<{
    text: string;
    filename: string;
    score: number;
  }>;
}

// Radix's viewport wraps its children in a `display: table` box, which grows to the widest
// unbreakable value — a tool card's 200-character source link made the whole transcript wider
// than the dialog, clipping the agent's lines and pushing every caller line off-screen. A block
// box keeps the transcript at the viewport's width.
const TRANSCRIPT_SCROLL_AREA = "flex-1 px-4 sm:px-6 pb-4 [&>[data-radix-scroll-area-viewport]>div]:!block";

// Key-Value Display Component
function KeyValueDisplay({ data, level = 0 }: { data: any; level?: number }) {
  if (data === null || data === undefined) {
    return <span className="text-muted-foreground italic">Not provided</span>;
  }

  if (typeof data === 'boolean') {
    return (
      <Badge variant={data ? "default" : "secondary"} className="font-normal">
        {data ? 'Yes' : 'No'}
      </Badge>
    );
  }

  if (typeof data === 'number') {
    return <span className="font-mono text-primary">{data}</span>;
  }

  if (typeof data === 'string') {
    if (data.length === 0) {
      return <span className="text-muted-foreground italic">Empty</span>;
    }
    return <span className="text-foreground [overflow-wrap:anywhere]">{data}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span className="text-muted-foreground italic">No items</span>;
    }

    // Simple array of primitives
    if (data.every(item => typeof item !== 'object')) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {data.map((item, idx) => (
            <Badge key={idx} variant="outline" className="font-normal">
              {String(item)}
            </Badge>
          ))}
        </div>
      );
    }

    // Array of objects
    return (
      <div className="space-y-2 mt-1">
        {data.map((item, idx) => (
          <div key={idx} className="pl-3 border-l-2 border-border/50">
            <span className="text-xs text-muted-foreground mb-1 block">Item {idx + 1}</span>
            <KeyValueDisplay data={item} level={level + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return <span className="text-muted-foreground italic">No data</span>;
    }

    return (
      <div className={`space-y-3 ${level > 0 ? 'mt-1' : ''}`}>
        {entries.map(([key, value]) => (
          <div key={key} className="group">
            <div className="flex items-start gap-3">
              <div className="flex items-center gap-2 min-w-[120px] sm:min-w-[160px]">
                {getIconForKey(key)}
                <span className="text-sm font-medium text-muted-foreground capitalize">
                  {formatKeyName(key)}
                </span>
              </div>
              <div className="flex-1 min-w-0 text-sm">
                <KeyValueDisplay data={value} level={level + 1} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
}

function formatKeyName(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());
}

function getIconForKey(key: string) {
  const lowerKey = key.toLowerCase();
  const iconClass = "w-3.5 h-3.5 text-muted-foreground/70";

  if (lowerKey.includes('name') || lowerKey.includes('user')) return <User className={iconClass} />;
  if (lowerKey.includes('phone') || lowerKey.includes('number')) return <Phone className={iconClass} />;
  if (lowerKey.includes('date') || lowerKey.includes('time') || lowerKey.includes('at')) return <Calendar className={iconClass} />;
  if (lowerKey.includes('message') || lowerKey.includes('text') || lowerKey.includes('content')) return <MessageSquare className={iconClass} />;
  if (lowerKey.includes('type') || lowerKey.includes('category')) return <Type className={iconClass} />;
  if (lowerKey.includes('count') || lowerKey.includes('amount') || lowerKey.includes('id')) return <Hash className={iconClass} />;
  if (lowerKey.includes('enabled') || lowerKey.includes('active') || lowerKey.includes('status')) return <ToggleLeft className={iconClass} />;
  if (lowerKey.includes('list') || lowerKey.includes('items') || lowerKey.includes('array')) return <List className={iconClass} />;

  return <FileText className={iconClass} />;
}

// Format a number of seconds as "HH:MM:SS" (elapsed time within a call).
function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

// Convert an absolute UTC "HH:MM:SS" wall-clock timestamp to elapsed time since
// the call's started_at, matching the enhanced-transcript convention (00:00:00-based).
function clockToElapsed(clock: string, startedAt?: string): string {
  const parts = clock.split(':').map(Number);
  const start = new Date(startedAt);
  const startSecs = start.getUTCHours() * 3600 + start.getUTCMinutes() * 60 + start.getUTCSeconds();
  const lineSecs = parts[0] * 3600 + parts[1] * 60 + parts[2];
  let diff = lineSecs - startSecs;
  if (diff < -12 * 3600) diff += 24 * 3600; // call crossed UTC midnight
  if (diff < 0) diff = 0;                    // minor clock skew before first line
  return formatElapsed(diff);
}

const toSecs = (ts?: string) => ts?.match(/^\d+:\d+:\d+$/) ? ts.split(':').reduce((a, n) => a * 60 + +n, 0) : null;

// Shift every line so the first one starts at 00:00:00 — lines up the transcript
// with the recording, which only begins once audio flows (after bot warm-up).
function anchorToFirstUtterance(items: TranscriptItem[]): TranscriptItem[] {
  const offset = Math.min(...items.map(i => toSecs(i.timestamp) ?? Infinity));
  if (!isFinite(offset) || offset <= 0) return items;
  return items.map(i => {
    const s = toSecs(i.timestamp);
    return s === null ? i : { ...i, timestamp: formatElapsed(s - offset) };
  });
}

export function CallDetailsDialog({ call: callProp, open, onOpenChange, onRefresh }: CallDetailsDialogProps) {
  const [toolExecutions, setToolExecutions] = useState<ToolExecution[]>([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [fixingTranscript, setFixingTranscript] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // The list query is lean, so we fetch the full row on demand when the dialog
  // opens. React Query keys on the id, so switching calls resets automatically.
  // Layer the latest lean list fields (callProp) over the fetched detail — the
  // lean row omits heavy keys, so they don't clobber the fetched ones.
  const { data: detail, refetch: refetchDetail } = useCallDetails(callProp?.id, open);
  const call = detail ? { ...detail, ...callProp } : callProp;

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      // Refetch the full call detail, and refresh the list so CallHistory
      // re-syncs the lean list fields for the selected row.
      refetchDetail();
      await Promise.resolve(onRefresh());
    } finally {
      // brief min-spin so the feedback is visible even on a fast refetch
      setTimeout(() => setRefreshing(false), 400);
    }
  };

  const handleFixTranscript = async () => {
    setFixingTranscript(true);
    try {
      await fixTranscript(call.id);
      toast.success("Transcript enhanced successfully");
      refetchDetail();
      onRefresh?.();
    } catch (err) {
      toast.error("Failed to regenerate transcript");
    } finally {
      setFixingTranscript(false);
    }
  };

  const handleCopyCallId = async () => {
    const text = call.id;
    const fallbackCopy = (value: string): boolean => {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        // iOS Safari needs a selection range, not just .select()
        const range = document.createRange();
        range.selectNodeContents(textarea);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        textarea.setSelectionRange(0, value.length);
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        return ok;
      } catch {
        return false;
      }
    };

    let copied = false;
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        copied = true;
      } else {
        copied = fallbackCopy(text);
      }
    } catch {
      copied = fallbackCopy(text);
    }

    if (copied) {
      setCopiedId(true);
      toast.success("Call ID copied to clipboard");
      setTimeout(() => setCopiedId(false), 2000);
    } else {
      toast.error("Failed to copy Call ID");
    }
  };

  useEffect(() => {
    if (call?.id && open) {
      setLoadingTools(true);
      getToolExecutions<ToolExecution>(call.id)
        .then(setToolExecutions)
        .catch((error) => console.error('Error fetching tool executions:', error))
        .finally(() => setLoadingTools(false));
    }
  }, [call?.id, open]);

  // Parse JSON transcript format
  const parseJsonTranscript = (transcriptJson: { conversation: Array<any> }, toolExecutions: ToolExecution[], startedAt?: string): TranscriptItem[] => {
    try {
      const startTime = startedAt ? new Date(startedAt).getTime() : 0;
      console.log('📝 Parsing JSON transcript with', transcriptJson.conversation.length, 'entries', startedAt ? `started at ${startedAt}` : 'no start time');

      const parsed = transcriptJson.conversation.map((entry, idx) => {
        if (entry.role === 'tool') {
          // Build tool args from query (request)
          const toolArgs: any = {};
          if (entry.query) {
            toolArgs.query = entry.query;
          }
          if (entry.args && typeof entry.args === 'object') {
            Object.assign(toolArgs, entry.args);
          }

          // Convert entry.timestamp (HH:MM:SS) to seconds from start
          let entrySeconds = -1;
          if (entry.timestamp) {
            const cleanTimestamp = entry.timestamp.replace(/[\[\]]/g, '').trim();
            const parts = cleanTimestamp.split(':');
            if (parts.length === 3) {
              entrySeconds = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
            } else if (parts.length === 2) {
              entrySeconds = parseInt(parts[0]) * 60 + parseInt(parts[1]);
            }
          }

          // Find matching tool execution to get status_code and grounding_chunks
          const matchingTool = toolExecutions.find(t => {
            if (t.tool_name !== entry.tool_name) return false;
            
            // If we have a valid entry timestamp and call start time, compare relative times
            if (entrySeconds >= 0 && startTime > 0) {
              const toolTime = new Date(t.executed_at).getTime();
              const toolRelativeSeconds = Math.floor((toolTime - startTime) / 1000);
              const diff = Math.abs(toolRelativeSeconds - entrySeconds);
              
              // Allow generous 30 second window for matching (timestamps can be fuzzy)
              const matches = diff < 30;
              if (matches) {
                console.log(`✅ Matched tool ${t.tool_name} at entry time ${entry.timestamp} (diff: ${diff}s)`);
              }
              return matches;
            }
            
            return true; // Match by name if timing unavailable
          });

          // Extract documents from tool execution result if available
          let toolDocs = entry.documents || null;
          if ((!toolDocs || toolDocs.length === 0) && matchingTool?.result) {
            console.log(`🔍 Attempting to extract docs from matching tool result for ${entry.tool_name}`);
            const resultObj = matchingTool.result as any;
            if (resultObj.grounding_chunks && Array.isArray(resultObj.grounding_chunks)) {
              toolDocs = resultObj.grounding_chunks;
            } else if (resultObj.documents && Array.isArray(resultObj.documents)) {
              toolDocs = resultObj.documents;
            } else if (Array.isArray(resultObj)) {
              // Check if the array itself contains grounding_chunks or is the list of docs
              if (resultObj.length > 0 && resultObj[0].grounding_chunks) {
                toolDocs = resultObj[0].grounding_chunks;
              } else {
                toolDocs = resultObj;
              }
            }
          }

          const toolItem = {
            id: `tool-json-${idx}`,
            role: 'Tool' as const,
            text: entry.query || entry.description || `Executed ${entry.tool_name || 'tool'}`,
            timestamp: entry.timestamp,
            toolName: entry.tool_name,
            toolArgs: Object.keys(toolArgs).length > 0 ? toolArgs : null,
            toolResult: entry.result,
            toolError: entry.error,
            isToolExecution: true,
            toolStatusCode: entry.status_code || matchingTool?.status_code || null,
            documents: Array.isArray(toolDocs) && toolDocs.length > 0 ? toolDocs : null,
          };

          console.log(`🔧 Tool entry ${idx}: ${entry.tool_name}`, {
            foundMatch: !!matchingTool,
            hasDocs: !!toolItem.documents,
            docsCount: toolItem.documents?.length || 0,
            timestamp: entry.timestamp,
            entrySeconds,
            matchingToolTime: matchingTool ? new Date(matchingTool.executed_at).toLocaleTimeString() : 'none'
          });

          return toolItem;
        } else if (entry.role === 'user') {
          // Filter out tool result content that might have been incorrectly included
          const content = entry.content || '';
          const looksLikeToolResult = /^\d+\.\s+/.test(content) ||
            /^(Based on the knowledge base|Please ensure|According to)/i.test(content);

          if (looksLikeToolResult) {
            console.warn(`⚠️ Skipping user entry ${idx} - looks like tool result content:`, content.substring(0, 100));
            return null;
          }

          return {
            id: `user-json-${idx}`,
            role: 'User' as const,
            text: content,
            timestamp: entry.timestamp,
          };
        } else if (entry.role === 'agent') {
          // Filter out tool result content that might have been incorrectly included in agent messages
          const content = entry.content || '';
          const looksLikeToolResult = /^\d+\.\s+/.test(content) ||
            /^(Based on the knowledge base|Please ensure|According to)/i.test(content) ||
            content.includes('A student who wishes to withdraw');

          if (looksLikeToolResult) {
            console.warn(`⚠️ Skipping agent entry ${idx} - looks like tool result content:`, content.substring(0, 100));
            return null;
          }

          return {
            id: `agent-json-${idx}`,
            role: 'Agent' as const,
            text: content,
            timestamp: entry.timestamp,
          };
        }
        return null;
      }).filter(Boolean) as TranscriptItem[];

      console.log('✅ Parsed JSON transcript:', {
        total: parsed.length,
        tools: parsed.filter(i => i.isToolExecution).length,
        users: parsed.filter(i => i.role === 'User').length,
        agents: parsed.filter(i => i.role === 'Agent').length,
      });

      return parsed;
    } catch (error) {
      console.error('❌ Error parsing JSON transcript:', error);
      return [];
    }
  };

  // Parse text transcript format (fallback)
  // Raw transcripts come in two flavors: absolute UTC wall-clock "[09:00:08]"
  // and already-elapsed "[00:00:00]". We only run clockToElapsed on the absolute
  // kind — telling them apart by whether the latest line is still below the
  // start-of-call wall-clock seconds (true only for elapsed timestamps).
  const parseTextTranscript = (transcript: string, startedAt?: string): TranscriptItem[] => {
    try {
      const lines = transcript.split('\n').filter(line => line.trim());

      const startSecs = startedAt ? toSecs(new Date(startedAt).toISOString().slice(11, 19)) : 0;
      const maxLineSecs = Math.max(0, ...lines.map(l => {
        const m = l.match(/\[(\d{2}:\d{2}:\d{2})\]/);
        return m ? toSecs(m[1]) : 0;
      }));
      const absolute = startSecs > 0 && maxLineSecs >= startSecs;

      return lines.map((line, idx) => {
        // Match regular user/agent messages with labels: [HH:MM:SS] User/Agent: text
        const matchWithLabel = line.match(/\[(\d{2}:\d{2}:\d{2})\]\s*(User|Agent):\s*(.+)$/);
        if (matchWithLabel) {
          const role = matchWithLabel[2] as 'User' | 'Agent';
          return {
            role: role,
            text: matchWithLabel[3],
            id: `transcript-${idx}`,
            timestamp: absolute ? clockToElapsed(matchWithLabel[1], startedAt) : matchWithLabel[1]
          } as TranscriptItem;
        }
        
        // Match simple format without timestamps
        const matchSimple = line.match(/^(User|Agent):\s*(.+)$/);
        if (matchSimple) {
          return {
            role: matchSimple[1] as 'User' | 'Agent',
            text: matchSimple[2],
            id: `transcript-${idx}`
          } as TranscriptItem;
        }
        
        return null;
      }).filter((item): item is TranscriptItem => item !== null);
    } catch (e) {
      console.error('Error parsing text transcript:', e);
      return [];
    }
  };

  // Check if we have a proper enhanced transcript available
  const hasProperTranscript = useMemo(() => {
    if (!call) return false;
    
    // Check for JSON transcript
    if (call.transcript_json) {
      const jsonData = typeof call.transcript_json === 'string' 
        ? (() => { try { return JSON.parse(call.transcript_json); } catch { return null; } })()
        : call.transcript_json;
      if (jsonData?.conversation?.length > 0) return true;
    }
    
    // Check for raw transcript
    if (call.transcript && call.transcript.trim().length > 0) return true;

    // Check for enhanced transcript
    if (call.enhanced_transcript && call.enhanced_transcript.trim().length > 0) return true;

    return false;
  }, [call?.transcript_json, call?.transcript, call?.enhanced_transcript]);

  const mergedTranscriptData = useMemo(() => {
    if (!call) return [];

    const sortByTimestamp = (items: TranscriptItem[]) =>
      items.sort((a, b) => {
        if (a.timestamp && b.timestamp) return a.timestamp.localeCompare(b.timestamp);
        if (a.timestamp) return -1;
        if (b.timestamp) return 1;
        return 0;
      });

    // Convert a DB tool_execution_logs row into a renderable Tool transcript item.
    // executed_at is stored in UTC; transcript timestamps are UTC HH:MM:SS, so we
    // slice the ISO string to interleave tool cards in the right place.
    const toolItemsFromDb = (execs: ToolExecution[]): TranscriptItem[] =>
      execs.map((t, idx) => {
        let timestamp: string | undefined;
        if (t.executed_at) {
          try {
            const startMs = call.started_at ? new Date(call.started_at).getTime() : 0;
            timestamp = startMs > 0
              ? formatElapsed((new Date(t.executed_at).getTime() - startMs) / 1000)
              : new Date(t.executed_at).toISOString().slice(11, 19);
          } catch { /* leave undefined */ }
        }

        let documents: any = null;
        const r = t.result as any;
        if (r && typeof r === 'object') {
          if (Array.isArray(r.grounding_chunks)) documents = r.grounding_chunks;
          else if (Array.isArray(r.documents)) documents = r.documents;
        }

        return {
          id: `tool-db-${t.id || idx}`,
          role: 'Tool' as const,
          text: t.description || `Executed ${t.tool_name || 'tool'}`,
          timestamp,
          toolName: t.tool_name,
          toolArgs: t.args || null,
          toolResult: t.result,
          toolError: t.error,
          isToolExecution: true,
          toolStatusCode: t.status_code ?? null,
          documents: Array.isArray(documents) && documents.length > 0 ? documents : null,
        };
      });

    // Merge DB tool executions into the base transcript when the chosen source
    // doesn't already carry tool entries (e.g. raw/enhanced text transcripts,
    // which only contain User/Agent lines). Avoids dropping tool logs entirely.
    const withTools = (baseItems: TranscriptItem[]) => {
      const alreadyHasTools = baseItems.some(i => i.isToolExecution);
      const merged = alreadyHasTools
        ? baseItems
        : [...baseItems, ...toolItemsFromDb(toolExecutions)];
      return sortByTimestamp(merged);
    };

    // 1. Check for JSON transcript
    let transcriptJsonData: { conversation: Array<any> } | null = null;
    if (call.transcript_json) {
      if (typeof call.transcript_json === 'string') {
        try {
          transcriptJsonData = JSON.parse(call.transcript_json);
        } catch (e) {
          console.error('❌ Failed to parse transcript_json string:', e);
        }
      } else if (typeof call.transcript_json === 'object' && call.transcript_json !== null) {
        transcriptJsonData = call.transcript_json as { conversation: Array<any> };
      }
    }

    const hasJsonTranscript = transcriptJsonData &&
      transcriptJsonData.conversation &&
      Array.isArray(transcriptJsonData.conversation) &&
      transcriptJsonData.conversation.length > 0;

    const hasRawTranscript = call.transcript && call.transcript.trim().length > 0;
    const hasEnhancedTranscript = call.enhanced_transcript && call.enhanced_transcript.trim().length > 0;

    // Priority: raw transcript > transcript_json > enhanced_transcript
    // If manual "Fix Transcript" was used (transcript_fix_count > 0) and transcript_json exists, prefer that
    const useJsonFirst = hasJsonTranscript && (call.transcript_fix_count || 0) > 0;

    if (useJsonFirst && transcriptJsonData) {
      console.log('✅ Using JSON transcript (manually fixed):', transcriptJsonData.conversation.length, 'entries');
      return withTools(parseJsonTranscript(transcriptJsonData, toolExecutions, call.started_at));
    }

    if (hasRawTranscript) {
      console.log('✅ Using raw transcript, length:', call.transcript!.length);
      return withTools(parseTextTranscript(call.transcript!, call.started_at));
    }

    if (hasJsonTranscript && transcriptJsonData) {
      console.log('✅ Using JSON transcript (no raw available):', transcriptJsonData.conversation.length, 'entries');
      return withTools(parseJsonTranscript(transcriptJsonData, toolExecutions, call.started_at));
    }

    if (!hasEnhancedTranscript) {
      console.log('⏳ No transcript available yet');
      // Even with no transcript text, show tool executions if we have them.
      return toolExecutions && toolExecutions.length > 0
        ? withTools([])
        : [];
    }

    console.log('📝 Using enhanced text transcript, Length:', call.enhanced_transcript!.length);
    const transcriptItems: TranscriptItem[] = parseTextTranscript(call.enhanced_transcript!);

    console.log('📝 Parsed transcript items:', transcriptItems.length);

    return withTools(transcriptItems);
  }, [call?.transcript, call?.enhanced_transcript, call?.transcript_json, call?.transcript_fix_count, toolExecutions, call?.started_at]);

  // Transcript tab only: re-anchor so the first line starts at 00:00:00 (matches
  // the recording). The Enhanced tab is left untouched — it's already 00:00:00-based.
  const transcriptTabData = useMemo(
    () => anchorToFirstUtterance(mergedTranscriptData),
    [mergedTranscriptData]
  );

  if (!call) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] w-[95vw] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="flex-shrink-0 p-4 sm:p-6 pb-2 sm:pb-3 border-b bg-muted/30">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <DialogTitle className="text-lg sm:text-xl">Call Details</DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
                onClick={handleRefresh}
                disabled={refreshing}
                aria-label="Refresh call details"
                title="Refresh call details"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <div className="flex items-center gap-2 sm:pr-8">
              <Badge variant="outline" className="capitalize">
                {call.call_type}
              </Badge>
              {call.disconnection_reason && (
                <Badge
                  variant={
                    getDisconnectionReasonInfo(call.disconnection_reason)?.callStatus === 'error'
                      ? 'destructive'
                      : 'secondary'
                  }
                >
                  {formatDisconnectionReason(call.disconnection_reason)}
                </Badge>
              )}
            </div>
          </div>
          {call.id && (
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Hash className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="font-medium">Call ID:</span>
              <code className="font-mono bg-muted px-1.5 py-0.5 rounded text-[11px] truncate max-w-full">
                {call.id}
              </code>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
                onClick={handleCopyCallId}
                aria-label="Copy Call ID"
              >
                {copiedId ? (
                  <Check className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </Button>
            </div>
          )}
        </DialogHeader>

        <Tabs defaultValue="transcript" className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 px-4 sm:px-6 border-b bg-background">
            <TabsList className="w-full justify-start h-auto p-0 bg-transparent gap-1 sm:gap-4 overflow-x-auto">
              {['transcript', ...(call.enhanced_transcript?.trim() ? ['enhanced'] : []), 'analytics', 'structured', 'variables', 'summary', 'recording', 'webhooks'].map((tab) => (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  className="rounded-none border-b-2 border-transparent px-2 sm:px-3 py-2.5 text-xs sm:text-sm capitalize whitespace-nowrap data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  {tab === 'variables' ? 'Dynamic Variables' : tab}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="transcript" className="flex-1 min-h-0 p-0 overflow-auto">
            {call.recording_url && (
              <div className="flex items-center justify-end gap-2 px-4 sm:px-6 pt-3">
                {(call.transcript_fix_count || 0) >= 5 && (
                  <span className="text-xs text-muted-foreground">Limit reached</span>
                )}
                {(call.transcript_fix_count || 0) > 0 && call.transcript_fix_count < 5 && (
                  <span className="text-xs text-muted-foreground">{5 - call.transcript_fix_count} attempts left</span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFixTranscript}
                  disabled={fixingTranscript || (call.transcript_fix_count || 0) >= 5}
                >
                  {fixingTranscript ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Enhance Transcript
                </Button>
              </div>
            )}
            {(() => {
              // Check what transcript format we're using
              const hasJsonTranscript = call?.transcript_json && (call.transcript_json as any).conversation && Array.isArray((call.transcript_json as any).conversation) && (call.transcript_json as any).conversation.length > 0;
              const hasEnhancedTranscript = call?.enhanced_transcript && call.enhanced_transcript.trim().length > 0;

              // Debug: Log what we're using
              console.log('📊 Transcript check:', {
                hasJsonTranscript,
                hasEnhancedTranscript,
                jsonEntries: hasJsonTranscript ? (call.transcript_json as any).conversation.length : 0,
                mergedItems: mergedTranscriptData.length,
                toolItems: mergedTranscriptData.filter(i => i.isToolExecution).length,
                agentItems: mergedTranscriptData.filter(i => i.role === 'Agent').length,
                userItems: mergedTranscriptData.filter(i => i.role === 'User').length,
              });

              // If we have parsed items, show them
              if (transcriptTabData.length > 0) {
                return (
                  <div className="h-full flex flex-col">
                    <ScrollArea className={TRANSCRIPT_SCROLL_AREA}>
                      <div className="space-y-3 py-4 pr-4">
                        {transcriptTabData.map((item) => {
                          // CRITICAL: Check if this is a tool execution FIRST
                          if (item.isToolExecution || item.role === 'Tool') {
                            // Ensure it's marked as tool execution
                            const toolItem = { ...item, isToolExecution: true };

                            // Check for request (query field from JSON)
                            const hasRequest = toolItem.toolArgs && Object.keys(toolItem.toolArgs).length > 0;
                            const hasResponse = toolItem.toolResult && (
                              typeof toolItem.toolResult === 'object' && toolItem.toolResult !== null
                                ? Object.keys(toolItem.toolResult).length > 0
                                : String(toolItem.toolResult).trim().length > 0
                            );
                            const hasError = toolItem.toolError && String(toolItem.toolError).trim().length > 0;

                            return (
                              <div key={toolItem.id} className="flex justify-center w-full my-3">
                                <Card className={`w-full max-w-full border-dashed ${hasError ? 'border-destructive/50' : ''}`}>
                                  <CardHeader className={`p-3 pb-2 ${hasError ? 'bg-destructive/10' : 'bg-muted/30'}`}>
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                      <div className="flex items-center gap-2">
                                        {hasError
                                          ? <AlertTriangle className="w-4 h-4 text-destructive" />
                                          : <Wrench className="w-4 h-4 text-muted-foreground" />}
                                        <span className="font-medium text-sm">{toolItem.toolName || 'Unknown Tool'}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs">Tool</Badge>
                                        {toolItem.timestamp && (
                                          <span className="text-xs text-muted-foreground">{toolItem.timestamp}</span>
                                        )}
                                        {/* Show status code if available */}
                                        {toolItem.toolStatusCode !== null && toolItem.toolStatusCode !== undefined && (
                                          <Badge
                                            variant={toolItem.toolStatusCode >= 200 && toolItem.toolStatusCode < 300 ? 'default' : 'destructive'}
                                            className="text-xs"
                                          >
                                            {toolItem.toolStatusCode}
                                          </Badge>
                                        )}
                                        {/* No status code is recorded for setup-time failures (e.g. an
                                            invalid parameter schema), so without this the card looked
                                            identical to a successful call. */}
                                        {hasError && (toolItem.toolStatusCode === null || toolItem.toolStatusCode === undefined) && (
                                          <Badge variant="destructive" className="text-xs">Failed</Badge>
                                        )}
                                      </div>
                                    </div>
                                    {/* Error rows only: the description carries the reason and was
                                        never rendered before. Deliberately NOT shown on successful
                                        cards — their description is internal log phrasing
                                        ("Executing X", "X response received (HTTP 200)") and
                                        surfacing it would change every existing card. */}
                                    {hasError && toolItem.text && toolItem.text !== toolItem.toolName && (
                                      <p className="text-xs mt-1 text-destructive">
                                        {toolItem.text}
                                      </p>
                                    )}
                                  </CardHeader>
                                  <CardContent className="p-3">
                                    {/* Errors start expanded — a collapsed accordion hid the
                                        reason entirely, which is why invalid-schema warnings
                                        went unnoticed in prod. */}
                                    <Accordion
                                      type="multiple"
                                      className="w-full"
                                      defaultValue={hasError ? [`response-${toolItem.id}`] : []}
                                    >
                                      {/* Request Section */}
                                      {hasRequest && (
                                        <AccordionItem value={`request-${toolItem.id}`} className="border-b">
                                          <AccordionTrigger className="py-2 text-sm font-medium hover:no-underline">
                                            <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                                              <Send className="w-4 h-4" />
                                              <span>Request</span>
                                              <Badge variant="outline" className="text-xs ml-1">
                                                {typeof toolItem.toolArgs === 'object' && toolItem.toolArgs !== null
                                                  ? Object.keys(toolItem.toolArgs).length + ' field' + (Object.keys(toolItem.toolArgs).length !== 1 ? 's' : '')
                                                  : '1 field'}
                                              </Badge>
                                            </div>
                                          </AccordionTrigger>
                                          <AccordionContent className="pt-2 pb-3">
                                            <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-3 border border-blue-100 dark:border-blue-900/20">
                                              {typeof toolItem.toolArgs === 'object' && toolItem.toolArgs !== null ? (
                                                <KeyValueDisplay data={toolItem.toolArgs} />
                                              ) : (
                                                <p className="text-sm whitespace-pre-wrap break-words">{String(toolItem.toolArgs)}</p>
                                              )}
                                            </div>
                                          </AccordionContent>
                                        </AccordionItem>
                                      )}

                                      {/* Response Section */}
                                      {(hasResponse || hasError) && (
                                        <AccordionItem value={`response-${toolItem.id}`} className="border-b">
                                          <AccordionTrigger className="py-2 text-sm font-medium hover:no-underline">
                                            <div className={`flex items-center gap-2 ${hasError ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                                              {hasError ? (
                                                <>
                                                  <Reply className="w-4 h-4" />
                                                  <span>Error Response</span>
                                                </>
                                              ) : (
                                                <>
                                                  <CheckCircle2 className="w-4 h-4" />
                                                  <span>Response</span>
                                                  {typeof toolItem.toolResult === 'object' && toolItem.toolResult !== null && (
                                                    <Badge variant="outline" className="text-xs ml-1">
                                                      {Object.keys(toolItem.toolResult).length + ' field' + (Object.keys(toolItem.toolResult).length !== 1 ? 's' : '')}
                                                    </Badge>
                                                  )}
                                                  {typeof toolItem.toolResult === 'string' && (
                                                    <Badge variant="outline" className="text-xs ml-1">
                                                      {toolItem.toolResult.length} chars
                                                    </Badge>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          </AccordionTrigger>
                                          <AccordionContent className="pt-2 pb-3">
                                            {hasError ? (
                                              <div className="bg-destructive/10 text-destructive p-3 rounded-lg border border-destructive/20">
                                                <p className="text-sm whitespace-pre-wrap break-words">{String(toolItem.toolError)}</p>
                                              </div>
                                            ) : (
                                              <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-3 border border-green-100 dark:border-green-900/20">
                                                {typeof toolItem.toolResult === 'string' ? (
                                                  <p className="text-sm whitespace-pre-wrap break-words">{toolItem.toolResult}</p>
                                                ) : (
                                                  <KeyValueDisplay data={toolItem.toolResult} />
                                                )}
                                              </div>
                                            )}
                                          </AccordionContent>
                                        </AccordionItem>
                                      )}

                                      {/* Associated Documents Section */}
                                      {toolItem.documents && toolItem.documents.length > 0 && (
                                        <AccordionItem value={`documents-${toolItem.id}`} className="border-b">
                                          <AccordionTrigger className="py-2 text-sm font-medium hover:no-underline">
                                            <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                                              <FileText className="w-4 h-4" />
                                              <span>Associated Documents</span>
                                              <Badge variant="outline" className="text-xs ml-1">
                                                {toolItem.documents.length} doc{toolItem.documents.length !== 1 ? 's' : ''}
                                              </Badge>
                                            </div>
                                          </AccordionTrigger>
                                          <AccordionContent className="pt-2 pb-3">
                                            <div className="space-y-2">
                                              {toolItem.documents.map((doc, docIdx) => {
                                                // Handle the documents which may have different formats
                                                const docObj = doc as any;
                                                const text = typeof docObj === 'string' ? docObj : (docObj.text || docObj.content || JSON.stringify(docObj));
                                                const filename = typeof docObj === 'object' ? (docObj.filename || docObj.name || `Document ${docIdx + 1}`) : `Document ${docIdx + 1}`;
                                                const score = typeof docObj === 'object' && docObj.score !== undefined ? docObj.score : null;

                                                return (
                                                  <div key={docIdx} className="bg-purple-50 dark:bg-purple-900/10 rounded-lg p-3 border border-purple-100 dark:border-purple-900/20">
                                                    <div className="flex items-center justify-between mb-2">
                                                      <span className="text-xs font-medium text-purple-700 dark:text-purple-300">{filename}</span>
                                                      {score !== null && (
                                                        <Badge variant="outline" className="text-xs">
                                                          Score: {typeof score === 'number' ? score.toFixed(2) : score}
                                                        </Badge>
                                                      )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words line-clamp-4">{text}</p>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </AccordionContent>
                                        </AccordionItem>
                                      )}
                                    </Accordion>
                                    {!hasError && isSearchWithoutSources(toolItem.toolName, toolItem.toolResult) && (
                                      <p className="pt-2 text-xs text-muted-foreground">{NO_SOURCES_TEXT}</p>
                                    )}
                                  </CardContent>
                                </Card>
                              </div>
                            );
                          }

                          // Regular user/agent message
                          const isUser = item.role === 'User';
                          return (
                            <div
                              key={item.id}
                              className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[90%] sm:max-w-[75%] rounded-2xl px-4 py-3 ${isUser
                                  ? 'bg-primary text-primary-foreground rounded-br-sm'
                                  : 'bg-muted rounded-bl-sm'
                                  }`}
                              >
                                <div className="flex items-center gap-2 mb-1 opacity-70">
                                  <span className="text-xs font-medium">{item.role}</span>
                                  {item.timestamp && (
                                    <span className="text-xs">{item.timestamp}</span>
                                  )}
                                </div>
                                <p className="text-sm leading-relaxed">{item.text}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </div>
                );
              }

              // If enhanced transcript is available but failed to parse, show raw enhanced transcript
              if (hasEnhancedTranscript && call?.enhanced_transcript) {
                return (
                  <div className="h-full flex flex-col space-y-3 p-4">
                    <div className="flex-shrink-0 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-sm text-blue-900 dark:text-blue-100 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span className="font-medium">Enhanced Transcript</span>
                        <Badge variant="outline" className="ml-auto">AI Generated</Badge>
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                        This transcript was generated from the call recording using AI and includes accurate timestamps and tool calls.
                      </p>
                    </div>
                    <Card className="flex-1 overflow-hidden flex flex-col">
                      <CardHeader className="pb-3 flex-shrink-0">
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Enhanced Transcript
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-1 overflow-hidden p-0">
                        <ScrollArea className="h-full max-h-[600px] w-full p-4">
                          <pre className="text-sm font-mono whitespace-pre-wrap break-words bg-muted/30 p-4 rounded-lg">
                            {call.enhanced_transcript}
                          </pre>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </div>
                );
              }

              // No enhanced transcript yet - failed calls will never generate one
              const reasonInfo = getDisconnectionReasonInfo(call.disconnection_reason);
              const callFailed = call.status === 'failed' ||
                reasonInfo?.callStatus === 'not_connected' ||
                reasonInfo?.callStatus === 'error';

              if (callFailed) {
                return (
                  <div className="flex flex-col items-center justify-center text-muted-foreground py-12 px-4">
                    <FileText className="w-8 h-8 mb-4" />
                    <p className="text-base font-medium mb-1">No transcript available</p>
                    <p className="text-sm text-center">
                      This call {reasonInfo?.callStatus === 'not_connected' ? 'was not connected' : 'encountered an error'}, so no transcript was generated.
                    </p>
                  </div>
                );
              }

              // Only claim a transcript is coming when one actually is. Nothing
              // generates in the background: bot.py stopped auto-calling
              // generate-perfect-transcript to save Gemini cost, so the only
              // producer is the Enhance Transcript button above. This branch used
              // to promise "Generating transcript..." to every connected call that
              // had none, which spun forever — e.g. call 6dc8f726, answered and
              // hung up 1.5s later with nothing said, still "generating" 40 minutes
              // on. It is the last branch, so it also catches busy / no-answer /
              // cancelled, which carry no disconnection_reason for callFailed to
              // match.
              if (fixingTranscript) {
                return (
                  <div className="flex flex-col items-center justify-center text-muted-foreground py-12 px-4">
                    <div className="animate-pulse mb-4">
                      <FileText className="w-8 h-8" />
                    </div>
                    <p className="text-base font-medium mb-1">Generating transcript...</p>
                    <p className="text-sm text-center">The enhanced transcript is being generated from the call recording. This may take a moment.</p>
                  </div>
                );
              }

              const callInProgress = ['queued', 'ringing', 'in-progress'].includes(call.status);
              if (callInProgress) {
                return (
                  <div className="flex flex-col items-center justify-center text-muted-foreground py-12 px-4">
                    <div className="animate-pulse mb-4">
                      <FileText className="w-8 h-8" />
                    </div>
                    <p className="text-base font-medium mb-1">Call in progress</p>
                    <p className="text-sm text-center">The transcript appears once the call ends.</p>
                  </div>
                );
              }

              return (
                <div className="flex flex-col items-center justify-center text-muted-foreground py-12 px-4">
                  <FileText className="w-8 h-8 mb-4" />
                  <p className="text-base font-medium mb-1">No transcript available</p>
                  <p className="text-sm text-center">
                    {call.recording_url
                      ? 'Nothing was transcribed during this call. Use Enhance Transcript above to build one from the recording.'
                      : 'Nothing was transcribed during this call.'}
                  </p>
                </div>
              );
            })()}
          </TabsContent>

          {call.enhanced_transcript?.trim() && (
            <TabsContent value="enhanced" className="flex-1 min-h-0 p-0 overflow-auto">
              {(() => {
                const items = mergedTranscriptData.length > 0 && (call.transcript_fix_count || 0) > 0
                  ? mergedTranscriptData
                  : parseTextTranscript(call.enhanced_transcript!);

                if (items.length === 0) return null;

                return (
                  <div className="h-full flex flex-col">
                    <ScrollArea className={TRANSCRIPT_SCROLL_AREA}>
                      <div className="space-y-3 py-4 pr-4">
                        {items.map((item) => {
                          if (item.isToolExecution || item.role === 'Tool') {
                            return (
                              <div key={item.id} className="flex justify-center w-full my-3">
                                <Card className="w-full max-w-full border-dashed">
                                  <CardHeader className="p-3 pb-2 bg-muted/30">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                      <div className="flex items-center gap-2">
                                        <Wrench className="w-4 h-4 text-muted-foreground" />
                                        <span className="font-medium text-sm">{item.toolName || 'Unknown Tool'}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs">Tool</Badge>
                                        {item.timestamp && <span className="text-xs text-muted-foreground">{item.timestamp}</span>}
                                      </div>
                                    </div>
                                  </CardHeader>
                                </Card>
                              </div>
                            );
                          }
                          const isUser = item.role === 'User';
                          return (
                            <div key={item.id} className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[90%] sm:max-w-[75%] rounded-2xl px-4 py-3 ${isUser ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
                                <div className="flex items-center gap-2 mb-1 opacity-70">
                                  <span className="text-xs font-medium">{item.role}</span>
                                  {item.timestamp && <span className="text-xs">{item.timestamp}</span>}
                                </div>
                                <p className="text-sm leading-relaxed">{item.text}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </div>
                );
              })()}
            </TabsContent>
          )}

          <TabsContent value="analytics" className="mt-0 p-4 sm:p-6 flex-1 min-h-0 overflow-auto">
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Call Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: 'Status', value: call.status, icon: ToggleLeft },
                      { label: 'Type', value: call.call_type, icon: Type },
                      { label: 'Duration', value: call.duration ? `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}` : 'N/A', icon: Clock },
                      { label: 'From', value: call.from_number, icon: Phone },
                      { label: 'To', value: call.to_number, icon: Phone },
                      { label: 'Started', value: call.started_at ? new Date(call.started_at).toLocaleString() : 'N/A', icon: Calendar },
                    ].map((item) => (
                      <div key={item.label} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 min-w-0">
                        <item.icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">{item.label}</p>
                          <p className="text-sm font-medium capitalize break-all">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {call?.analysis_data && Object.keys(call.analysis_data as object).length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Analysis Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <KeyValueDisplay data={call.analysis_data} />
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="structured" className="mt-0 p-4 sm:p-6 flex-1 min-h-0 overflow-auto">
            {call?.analysis_data && Object.keys(call.analysis_data as object).length > 0 ? (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Structured Data</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(call.analysis_data as Record<string, any>).map(([key, value]) => (
                    <div key={key} className="border-b border-border/50 pb-4 last:border-0 last:pb-0">
                      <h4 className="text-sm font-semibold mb-3 capitalize flex items-center gap-2">
                        {getIconForKey(key)}
                        {formatKeyName(key)}
                      </h4>
                      <div className="pl-4 sm:pl-6">
                        <KeyValueDisplay data={value} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <div className="text-center text-muted-foreground py-12">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No structured data available</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="variables" className="mt-0 p-4 sm:p-6 flex-1 min-h-0 overflow-auto">
            {call?.dynamic_variables && Object.keys(call.dynamic_variables as object).length > 0 ? (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Dynamic Variables</CardTitle>
                </CardHeader>
                <CardContent>
                  <KeyValueDisplay data={call.dynamic_variables} />
                </CardContent>
              </Card>
            ) : (
              <div className="text-center text-muted-foreground py-12">
                <Type className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No dynamic variables were passed to this call</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="summary" className="mt-0 p-4 sm:p-6 flex-1 min-h-0 overflow-auto">
            {call?.summary || (call?.analysis_data && (call.analysis_data as any).summary) ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    <p className="leading-relaxed text-foreground">
                      {call?.summary || (call?.analysis_data as any).summary}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="text-center text-muted-foreground py-12">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No summary available</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="recording" className="mt-0 p-4 sm:p-6 flex-1 min-h-0 overflow-auto">
            {call?.call_type === 'web' ? (
              call?.recording_url ? (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border/50">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Video className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <video
                      controls
                      className="w-full h-10 rounded"
                      src={call.recording_url}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {call.duration ? `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}` : ''}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 text-muted-foreground">
                  <Video className="w-4 h-4 opacity-50" />
                  <span className="text-sm">No recording available</span>
                </div>
              )
            ) : call?.recording_url || call?.recording_sid ? (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border/50">
                <div className="p-2 rounded-full bg-primary/10">
                  <Volume2 className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  {call.recording_sid || call.recording_url?.startsWith('https://api.twilio.com/') ? (
                    <TwilioRecording callId={call.id} />
                  ) : (
                    <audio controls className="w-full h-8" src={call.recording_url!}>
                      Your browser does not support the audio element.
                    </audio>
                  )}
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {call.duration ? `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}` : ''}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 text-muted-foreground">
                <Volume2 className="w-4 h-4 opacity-50" />
                <span className="text-sm">No recording available</span>
              </div>
            )}
          </TabsContent>

          <TabsContent value="webhooks" className="flex-1 min-h-0 overflow-auto">
            {call?.id && (
              <WebhookEventsTab callId={call.id} agentId={call.agent_id} open={open} onRefresh={onRefresh} />
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
